import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  TechProfile,
  TechProfileListItem,
  TechCertification,
  TechDocument,
  TechSkill,
  AvailabilityEntry,
  CrewAssignment,
  TravelItinerary,
  TravelSegment,
  TravelRecord,
  CrewList,
  PaginatedResponse,
  CrewFinderResponse,
  CreateTechProfilePayload,
  UpdateTechProfilePayload,
  CreateTechCertificationPayload,
  CreateTechSkillPayload,
  CreateAvailabilityPayload,
  BulkAvailabilityPayload,
  CreateAssignmentPayload,
  CreateTravelItineraryPayload,
  UpdateTravelItineraryPayload,
  CreateTravelSegmentPayload,
  UpdateTravelSegmentPayload,
  CreateTravelRecordPayload,
  CreateCrewListPayload,
  CrewTemplate,
  CreateCrewTemplateFromJobPayload,
  ApplyCrewTemplatePayload,
  ApplyCrewTemplateResponse,
  TechStatus,
  TechCertType,
  AssignmentStatus,
  AvailabilityStatus,
  TravelType,
  OnboardingChecklist,
  OnboardingItem,
  TechOnboardingProgress,
  TechOnboardingItemProgress,
  IncompleteTechOnboarding,
  CreateOnboardingChecklistPayload,
  CreateOnboardingItemPayload,
  PerformanceSummary,
  TechPerformanceReview,
  CreatePerformanceReviewPayload,
  GPSCheckIn,
  OnSiteTech,
  CreateCheckInPayload,
  ProvisionUserPayload,
  ProvisionUserResponse,
} from '@/lib/types';

const CREW_API = '/api/v1/crew';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface TechProfileFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: TechStatus | '';
  employment_type?: string;
  cert_type?: TechCertType | '';
  ordering?: string;
}

export interface TechCertificationFilters {
  page?: number;
  page_size?: number;
  cert_type?: TechCertType | '';
  status?: string;
  tech?: number;
}

export interface AvailabilityFilters {
  page?: number;
  page_size?: number;
  tech?: number;
  date_from?: string;
  date_to?: string;
  status?: AvailabilityStatus | '';
}

export interface AssignmentFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: AssignmentStatus | '';
  tech?: number;
  ordering?: string;
}

export interface TravelRecordFilters {
  page?: number;
  page_size?: number;
  travel_type?: TravelType | '';
  tech?: number;
  assignment?: number;
  upcoming?: boolean;
  date_from?: string;
  date_to?: string;
}

export interface TravelItineraryFilters {
  page?: number;
  page_size?: number;
  assignment?: number;
}

export interface CrewListFilters {
  page?: number;
  page_size?: number;
  q?: string;
}

export interface CrewTemplateFilters {
  page?: number;
  page_size?: number;
  q?: string;
}

export interface FindCrewFilters {
  project?: number;
  job?: number;
  start_date?: string;
  end_date?: string;
  required_cert_level?: number;
  required_skills?: string[] | string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

function normalizeFindCrewFilters(filters: FindCrewFilters): Record<string, unknown> {
  const requiredSkills = Array.isArray(filters.required_skills)
    ? filters.required_skills.join(',')
    : filters.required_skills;
  return cleanFilters({
    ...filters,
    required_skills: requiredSkills,
  });
}

// ─── Tech Profile Hooks ─────────────────────────────────────────────────────

export function useTechProfiles(filters: TechProfileFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.techProfiles.list(clean),
    queryFn: async (): Promise<PaginatedResponse<TechProfileListItem>> => {
      const response = await apiClient.get<PaginatedResponse<TechProfileListItem>>(
        `${CREW_API}/techs/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

export function useTechProfile(id: number) {
  return useQuery({
    queryKey: queryKeys.techProfiles.detail(id),
    queryFn: async (): Promise<TechProfile> => {
      const response = await apiClient.get<TechProfile>(`${CREW_API}/techs/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateTechProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTechProfilePayload): Promise<TechProfile> => {
      const response = await apiClient.post<TechProfile>(`${CREW_API}/techs/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.all });
      // The onboarding technician step completes from real tech records.
      qc.invalidateQueries({ queryKey: queryKeys.onboarding.state() });
    },
  });
}

export function useUpdateTechProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateTechProfilePayload;
    }): Promise<TechProfile> => {
      const response = await apiClient.patch<TechProfile>(`${CREW_API}/techs/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.detail(variables.id) });
    },
  });
}

// ─── Tech Certification Hooks ───────────────────────────────────────────────

export function useTechCertifications(filters: TechCertificationFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.techCertifications.list(clean),
    queryFn: async (): Promise<PaginatedResponse<TechCertification>> => {
      const response = await apiClient.get<PaginatedResponse<TechCertification>>(
        `${CREW_API}/certifications/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useAllTechCertifications(filters: TechCertificationFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: [...queryKeys.techCertifications.all, 'all-pages', clean] as const,
    queryFn: async (): Promise<PaginatedResponse<TechCertification>> => {
      const allFilters: TechCertificationFilters = { ...clean };
      delete allFilters.page;
      delete allFilters.page_size;

      const results: TechCertification[] = [];
      let count: number | undefined;
      let page = 1;

      do {
        const response = await apiClient.get<PaginatedResponse<TechCertification>>(
          `${CREW_API}/certifications/`,
          { params: { ...allFilters, page, page_size: 100 } }
        );
        count = response.data.count;
        results.push(...response.data.results);
        if (response.data.results.length === 0) break;
        page += 1;
      } while (count !== undefined && results.length < count);

      return { count: count ?? 0, next: null, previous: null, results };
    },
  });
}

export function useTechCertificationsForTech(techId: number) {
  return useQuery({
    queryKey: [...queryKeys.techProfiles.detail(techId), 'certifications'] as const,
    queryFn: async (): Promise<TechCertification[]> => {
      const response = await apiClient.get<TechCertification[]>(
        `${CREW_API}/techs/${techId}/certifications/`
      );
      return response.data;
    },
    enabled: techId > 0,
  });
}

export function useCreateCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTechCertificationPayload): Promise<TechCertification> => {
      const response = await apiClient.post<TechCertification>(
        `${CREW_API}/certifications/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.techCertifications.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.all });
      qc.invalidateQueries({ queryKey: queryKeys.techOnboarding.forTech(variables.tech) });
    },
  });
}

export function useCreateCertificationForTech() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      techId,
      data,
    }: {
      techId: number;
      data: Omit<CreateTechCertificationPayload, 'tech'>;
    }): Promise<TechCertification> => {
      const response = await apiClient.post<TechCertification>(
        `${CREW_API}/techs/${techId}/certifications/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.techCertifications.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.detail(variables.techId) });
      qc.invalidateQueries({ queryKey: queryKeys.techOnboarding.forTech(variables.techId) });
    },
  });
}

export function useUpdateTechCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      certificationId,
      techId: _techId,
      data,
    }: {
      certificationId: number;
      techId: number;
      data: Partial<CreateTechCertificationPayload>;
    }): Promise<TechCertification> => {
      const response = await apiClient.patch<TechCertification>(
        `${CREW_API}/certifications/${certificationId}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.techCertifications.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.detail(variables.techId) });
      qc.invalidateQueries({ queryKey: queryKeys.techOnboarding.forTech(variables.techId) });
    },
  });
}

export function useRenewTechCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      certificationId,
      techId: _techId,
      data,
    }: {
      certificationId: number;
      techId: number;
      data: Partial<CreateTechCertificationPayload>;
    }): Promise<TechCertification> => {
      const response = await apiClient.post<TechCertification>(
        `${CREW_API}/certifications/${certificationId}/renew/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.techCertifications.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.detail(variables.techId) });
      qc.invalidateQueries({ queryKey: queryKeys.techOnboarding.forTech(variables.techId) });
    },
  });
}

// ─── Tech Document Hooks ────────────────────────────────────────────────────

export function useTechDocuments(techId: number) {
  return useQuery({
    queryKey: [...queryKeys.techProfiles.detail(techId), 'documents'] as const,
    queryFn: async (): Promise<TechDocument[]> => {
      const response = await apiClient.get<TechDocument[]>(
        `${CREW_API}/techs/${techId}/documents/`
      );
      return response.data;
    },
    enabled: techId > 0,
  });
}

export function useUploadTechDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      techId,
      data,
    }: {
      techId: number;
      data: FormData;
    }): Promise<TechDocument> => {
      const response = await apiClient.post<TechDocument>(
        `${CREW_API}/techs/${techId}/documents/`,
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.detail(variables.techId) });
    },
  });
}

// ─── Tech Skill Hooks ───────────────────────────────────────────────────────

export function useTechSkills(techId: number) {
  return useQuery({
    queryKey: [...queryKeys.techProfiles.detail(techId), 'skills'] as const,
    queryFn: async (): Promise<TechSkill[]> => {
      const response = await apiClient.get<TechSkill[]>(
        `${CREW_API}/techs/${techId}/skills/`
      );
      return response.data;
    },
    enabled: techId > 0,
  });
}

export function useCreateTechSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      techId,
      data,
    }: {
      techId: number;
      data: CreateTechSkillPayload;
    }): Promise<TechSkill> => {
      const response = await apiClient.post<TechSkill>(
        `${CREW_API}/techs/${techId}/skills/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.detail(variables.techId) });
    },
  });
}

// ─── Availability Hooks ─────────────────────────────────────────────────────

export function useAvailability(filters: AvailabilityFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.availability.list(clean),
    queryFn: async (): Promise<PaginatedResponse<AvailabilityEntry>> => {
      const response = await apiClient.get<PaginatedResponse<AvailabilityEntry>>(
        `${CREW_API}/availability/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useTechAvailability(techId: number, dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: [...queryKeys.techProfiles.detail(techId), 'availability', dateFrom, dateTo] as const,
    queryFn: async (): Promise<AvailabilityEntry[]> => {
      const params: Record<string, string> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await apiClient.get<AvailabilityEntry[]>(
        `${CREW_API}/techs/${techId}/availability/`,
        { params }
      );
      return response.data;
    },
    enabled: techId > 0,
  });
}

export function useCreateAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateAvailabilityPayload): Promise<AvailabilityEntry> => {
      const response = await apiClient.post<AvailabilityEntry>(
        `${CREW_API}/availability/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.availability.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.all });
    },
  });
}

export function useBulkUpdateAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entries: BulkAvailabilityPayload[]): Promise<AvailabilityEntry[]> => {
      const response = await apiClient.post<AvailabilityEntry[]>(
        `${CREW_API}/availability/bulk-update/`,
        entries
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.availability.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.all });
    },
  });
}

// ─── Assignment Hooks ───────────────────────────────────────────────────────

export function useAssignments(filters: AssignmentFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.crewAssignments.list(clean),
    queryFn: async (): Promise<PaginatedResponse<CrewAssignment>> => {
      const response = await apiClient.get<PaginatedResponse<CrewAssignment>>(
        `${CREW_API}/assignments/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useFindCrew(filters: FindCrewFilters, options?: { enabled?: boolean }) {
  const clean = normalizeFindCrewFilters(filters);
  const enabled = Boolean((filters.project || filters.job) && filters.start_date && filters.end_date)
    && (options?.enabled ?? true);

  return useQuery({
    queryKey: queryKeys.crewFinders.list(clean),
    queryFn: async (): Promise<CrewFinderResponse> => {
      const response = await apiClient.get<CrewFinderResponse>(
        `${CREW_API}/techs/find-crew/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

export function useTravelItineraries(filters: TravelItineraryFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.travelItineraries.list(clean),
    queryFn: async (): Promise<PaginatedResponse<TravelItinerary>> => {
      const response = await apiClient.get<PaginatedResponse<TravelItinerary>>(
        `${CREW_API}/travel-itineraries/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useCreateTravelItinerary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTravelItineraryPayload): Promise<TravelItinerary> => {
      const response = await apiClient.post<TravelItinerary>(`${CREW_API}/travel-itineraries/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.travelItineraries.all });
      qc.invalidateQueries({ queryKey: ['my-travel'] });
      qc.invalidateQueries({ queryKey: queryKeys.crewAssignments.detail(variables.assignment) });
      qc.invalidateQueries({ queryKey: queryKeys.crewAssignments.all });
    },
  });
}

export function useUpdateTravelItinerary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateTravelItineraryPayload;
    }): Promise<TravelItinerary> => {
      const response = await apiClient.patch<TravelItinerary>(`${CREW_API}/travel-itineraries/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.travelItineraries.all });
      qc.invalidateQueries({ queryKey: queryKeys.travelItineraries.detail(variables.id) });
      qc.invalidateQueries({ queryKey: queryKeys.crewAssignments.all });
    },
  });
}

export function useCreateTravelSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTravelSegmentPayload): Promise<TravelSegment> => {
      const response = await apiClient.post<TravelSegment>(`${CREW_API}/travel-segments/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.travelItineraries.all });
      qc.invalidateQueries({ queryKey: queryKeys.travelItineraries.detail(variables.itinerary) });
      qc.invalidateQueries({ queryKey: queryKeys.crewAssignments.all });
    },
  });
}

export function useUpdateTravelSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateTravelSegmentPayload;
    }): Promise<TravelSegment> => {
      const response = await apiClient.patch<TravelSegment>(`${CREW_API}/travel-segments/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.travelItineraries.all });
      qc.invalidateQueries({ queryKey: queryKeys.crewAssignments.all });
    },
  });
}

export function useDeleteTravelSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, itineraryId: _itineraryId }: { id: number; itineraryId: number }): Promise<void> => {
      await apiClient.delete(`${CREW_API}/travel-segments/${id}/`);
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.travelItineraries.all });
      qc.invalidateQueries({ queryKey: queryKeys.travelItineraries.detail(variables.itineraryId) });
      qc.invalidateQueries({ queryKey: queryKeys.crewAssignments.all });
    },
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateAssignmentPayload): Promise<CrewAssignment> => {
      const response = await apiClient.post<CrewAssignment>(`${CREW_API}/assignments/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.crewAssignments.all });
      qc.invalidateQueries({ queryKey: queryKeys.availability.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.all });
    },
  });
}

// ─── Travel Record Hooks ────────────────────────────────────────────────────

export function useTravelRecords(filters: TravelRecordFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.travelRecords.list(clean),
    queryFn: async (): Promise<PaginatedResponse<TravelRecord>> => {
      const response = await apiClient.get<PaginatedResponse<TravelRecord>>(
        `${CREW_API}/travel/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useAddTravelToAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assignmentId,
      data,
    }: {
      assignmentId: number;
      data: Omit<CreateTravelRecordPayload, 'tech' | 'assignment'>;
    }): Promise<TravelRecord> => {
      const response = await apiClient.post<TravelRecord>(
        `${CREW_API}/assignments/${assignmentId}/add-travel/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.travelRecords.all });
      qc.invalidateQueries({ queryKey: queryKeys.crewAssignments.detail(variables.assignmentId) });
    },
  });
}

// Backend returns hydrated members as `members_detail` while the public
// `CrewList` type expects `members`; `member_ids` is write-only on the server.
type RawCrewList = Omit<CrewList, 'members' | 'member_ids'> & {
  members?: TechProfileListItem[];
  members_detail?: TechProfileListItem[];
  member_ids?: number[];
};

function normalizeCrewList(raw: RawCrewList): CrewList {
  const members = raw.members_detail ?? raw.members ?? [];
  return {
    ...raw,
    members,
    member_ids: members.map((m) => m.id),
  };
}

// ─── Crew List Hooks ────────────────────────────────────────────────────────

export function useCrewLists(filters: CrewListFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.crewLists.list(clean),
    queryFn: async (): Promise<PaginatedResponse<CrewList>> => {
      const response = await apiClient.get<PaginatedResponse<RawCrewList>>(
        `${CREW_API}/crew-lists/`,
        { params: clean }
      );
      return {
        ...response.data,
        results: response.data.results.map(normalizeCrewList),
      };
    },
  });
}

export function useCrewList(id: number) {
  return useQuery({
    queryKey: queryKeys.crewLists.detail(id),
    queryFn: async (): Promise<CrewList> => {
      const response = await apiClient.get<RawCrewList>(`${CREW_API}/crew-lists/${id}/`);
      return normalizeCrewList(response.data);
    },
    enabled: id > 0,
  });
}

export function useCreateCrewList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateCrewListPayload): Promise<CrewList> => {
      const response = await apiClient.post<RawCrewList>(`${CREW_API}/crew-lists/`, data);
      return normalizeCrewList(response.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.crewLists.all });
    },
  });
}

export function useUpdateCrewList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateCrewListPayload>;
    }): Promise<CrewList> => {
      const response = await apiClient.patch<RawCrewList>(`${CREW_API}/crew-lists/${id}/`, data);
      return normalizeCrewList(response.data);
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.crewLists.all });
      qc.invalidateQueries({ queryKey: queryKeys.crewLists.detail(variables.id) });
    },
  });
}

export function useAddCrewListMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      listId,
      techId,
    }: {
      listId: number;
      techId: number;
    }): Promise<CrewList> => {
      const response = await apiClient.post<RawCrewList>(
        `${CREW_API}/crew-lists/${listId}/add-member/`,
        { tech_id: techId }
      );
      return normalizeCrewList(response.data);
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.crewLists.detail(variables.listId) });
      qc.invalidateQueries({ queryKey: queryKeys.crewLists.all });
    },
  });
}

export function useRemoveCrewListMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      listId,
      techId,
    }: {
      listId: number;
      techId: number;
    }): Promise<CrewList> => {
      const response = await apiClient.post<RawCrewList>(
        `${CREW_API}/crew-lists/${listId}/remove-member/`,
        { tech_id: techId }
      );
      return normalizeCrewList(response.data);
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.crewLists.detail(variables.listId) });
      qc.invalidateQueries({ queryKey: queryKeys.crewLists.all });
    },
  });
}

// ─── Crew Template Hooks ────────────────────────────────────────────────────

export function useCrewTemplates(filters: CrewTemplateFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.crewTemplates.list(clean),
    queryFn: async (): Promise<PaginatedResponse<CrewTemplate>> => {
      const response = await apiClient.get<PaginatedResponse<CrewTemplate>>(
        `${CREW_API}/crew-templates/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useCreateCrewTemplateFromJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateCrewTemplateFromJobPayload): Promise<CrewTemplate> => {
      const response = await apiClient.post<CrewTemplate>(
        `${CREW_API}/crew-templates/from-project/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.crewTemplates.all });
    },
  });
}

export function useApplyCrewTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: ApplyCrewTemplatePayload;
    }): Promise<ApplyCrewTemplateResponse> => {
      const response = await apiClient.post<ApplyCrewTemplateResponse>(
        `${CREW_API}/crew-templates/${id}/apply/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.crewTemplates.all });
      qc.invalidateQueries({ queryKey: queryKeys.crewAssignments.all });
      qc.invalidateQueries({ queryKey: queryKeys.crewFinders.all });
    },
  });
}

// ─── My Availability Hooks (Self-Service) ──────────────────────────────────

export interface MyAvailabilityFilters {
  date_from?: string;
  date_to?: string;
}

export function useMyAvailability(filters: MyAvailabilityFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: [...queryKeys.availability.all, 'my', clean] as const,
    queryFn: async (): Promise<AvailabilityEntry[]> => {
      const response = await apiClient.get<AvailabilityEntry[]>(
        `${CREW_API}/techs/my-availability/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useUpdateMyAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      entries: Array<{ date: string; status: AvailabilityStatus; notes?: string }>
    ): Promise<AvailabilityEntry[]> => {
      const response = await apiClient.post<AvailabilityEntry[]>(
        `${CREW_API}/techs/my-availability/`,
        entries
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.availability.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.all });
    },
  });
}

// ─── Expiring Certs Hook ────────────────────────────────────────────────────

export function useExpiringCerts(days: number = 60) {
  return useQuery({
    queryKey: [...queryKeys.techCertifications.all, 'expiring', days] as const,
    queryFn: async (): Promise<PaginatedResponse<TechCertification>> => {
      const response = await apiClient.get<PaginatedResponse<TechCertification>>(
        `${CREW_API}/certifications/`,
        { params: { status: 'expiring_soon', days, page_size: 100 } }
      );
      return response.data;
    },
  });
}

export function useExpiredCerts() {
  return useQuery({
    queryKey: [...queryKeys.techCertifications.all, 'expired'] as const,
    queryFn: async (): Promise<PaginatedResponse<TechCertification>> => {
      const response = await apiClient.get<PaginatedResponse<TechCertification>>(
        `${CREW_API}/certifications/`,
        { params: { status: 'expired', page_size: 100 } }
      );
      return response.data;
    },
  });
}

// ─── Upcoming Travel Hook ──────────────────────────────────────────────────

export function useUpcomingTravel(filters: Omit<TravelRecordFilters, 'upcoming'> = {}) {
  const clean = cleanFilters({ ...filters, upcoming: true });
  return useQuery({
    queryKey: [...queryKeys.travelRecords.all, 'upcoming', clean] as const,
    queryFn: async (): Promise<PaginatedResponse<TravelRecord>> => {
      const response = await apiClient.get<PaginatedResponse<TravelRecord>>(
        `${CREW_API}/travel/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

// ─── Onboarding Hooks ──────────────────────────────────────────────────────

export function useOnboardingChecklists() {
  return useQuery({
    queryKey: queryKeys.onboardingChecklists.lists(),
    queryFn: async (): Promise<PaginatedResponse<OnboardingChecklist>> => {
      const response = await apiClient.get<PaginatedResponse<OnboardingChecklist>>(
        `${CREW_API}/onboarding-checklists/`
      );
      return response.data;
    },
  });
}

export function useCreateOnboardingChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateOnboardingChecklistPayload): Promise<OnboardingChecklist> => {
      const response = await apiClient.post<OnboardingChecklist>(
        `${CREW_API}/onboarding-checklists/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.onboardingChecklists.all });
    },
  });
}

export function useDeleteOnboardingChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${CREW_API}/onboarding-checklists/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.onboardingChecklists.all });
    },
  });
}

export function useAddOnboardingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      checklistId,
      data,
    }: {
      checklistId: number;
      data: CreateOnboardingItemPayload;
    }): Promise<void> => {
      await apiClient.post(
        `${CREW_API}/onboarding-checklists/${checklistId}/add-item/`,
        data
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.onboardingChecklists.all });
    },
  });
}

export function useUpdateOnboardingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      checklistId,
      itemId,
      data,
    }: {
      checklistId: number;
      itemId: number;
      data: Partial<CreateOnboardingItemPayload>;
    }): Promise<OnboardingItem> => {
      const response = await apiClient.patch<OnboardingItem>(
        `${CREW_API}/onboarding-checklists/${checklistId}/items/${itemId}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.onboardingChecklists.all });
    },
  });
}

export function useAssignOnboardingChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      checklistId,
      techId,
    }: {
      checklistId: number;
      techId: number;
    }): Promise<TechOnboardingProgress> => {
      const response = await apiClient.post<TechOnboardingProgress>(
        `${CREW_API}/onboarding-checklists/${checklistId}/assign-tech/`,
        { tech_id: techId }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.onboardingChecklists.all });
      qc.invalidateQueries({ queryKey: queryKeys.techOnboarding.all });
    },
  });
}

export function useTechOnboarding(techId: number) {
  return useQuery({
    queryKey: queryKeys.techOnboarding.forTech(techId),
    queryFn: async (): Promise<TechOnboardingProgress[]> => {
      const response = await apiClient.get<TechOnboardingProgress[]>(
        `${CREW_API}/techs/${techId}/onboarding/`
      );
      return response.data;
    },
    enabled: techId > 0,
  });
}

export function useCompleteOnboardingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemProgressId,
      notes,
    }: {
      itemProgressId: number;
      notes?: string;
    }): Promise<TechOnboardingItemProgress> => {
      const response = await apiClient.post<TechOnboardingItemProgress>(
        `${CREW_API}/onboarding-item-progress/${itemProgressId}/complete/`,
        { notes: notes || '' }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.techOnboarding.all });
      qc.invalidateQueries({ queryKey: queryKeys.onboardingChecklists.all });
    },
  });
}

export function useSkipOnboardingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemProgressId,
      notes,
    }: {
      itemProgressId: number;
      notes?: string;
    }): Promise<TechOnboardingItemProgress> => {
      const response = await apiClient.post<TechOnboardingItemProgress>(
        `${CREW_API}/onboarding-item-progress/${itemProgressId}/skip/`,
        { notes: notes || '' }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.techOnboarding.all });
      qc.invalidateQueries({ queryKey: queryKeys.onboardingChecklists.all });
    },
  });
}

export function useIncompleteTechsOnboarding() {
  return useQuery({
    queryKey: queryKeys.onboardingChecklists.incompleteTechs(),
    queryFn: async (): Promise<IncompleteTechOnboarding[]> => {
      const response = await apiClient.get<IncompleteTechOnboarding[]>(
        `${CREW_API}/onboarding-checklists/incomplete-techs/`
      );
      return response.data;
    },
  });
}

// ─── Performance Tracking Hooks ────────────────────────────────────────────

export function usePerformanceSummary(techId: number) {
  return useQuery({
    queryKey: queryKeys.performanceReviews.summary(techId),
    queryFn: async (): Promise<PerformanceSummary> => {
      const response = await apiClient.get<PerformanceSummary>(
        `${CREW_API}/techs/${techId}/performance-summary/`
      );
      return response.data;
    },
    enabled: techId > 0,
  });
}

export function useCreatePerformanceReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreatePerformanceReviewPayload): Promise<TechPerformanceReview> => {
      const response = await apiClient.post<TechPerformanceReview>(
        `${CREW_API}/performance-reviews/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.performanceReviews.all });
    },
  });
}

// ─── GPS / Location Check-In Hooks ─────────────────────────────────────────

export function useCheckIns(filters: { tech?: number; active?: boolean } = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.checkIns.list(clean),
    queryFn: async (): Promise<PaginatedResponse<GPSCheckIn>> => {
      const response = await apiClient.get<PaginatedResponse<GPSCheckIn>>(
        `${CREW_API}/check-ins/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useCreateCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateCheckInPayload): Promise<GPSCheckIn> => {
      const response = await apiClient.post<GPSCheckIn>(`${CREW_API}/check-ins/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.checkIns.all });
    },
  });
}

export function useCheckOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkInId: number): Promise<GPSCheckIn> => {
      const response = await apiClient.post<GPSCheckIn>(
        `${CREW_API}/check-ins/${checkInId}/check-out/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.checkIns.all });
    },
  });
}

export function useOnSiteTechs() {
  return useQuery({
    queryKey: queryKeys.checkIns.onSite(),
    queryFn: async (): Promise<OnSiteTech[]> => {
      const response = await apiClient.get<OnSiteTech[]>(
        `${CREW_API}/check-ins/on-site/`
      );
      return response.data;
    },
    refetchInterval: 30000,
  });
}

// ─── Provision User (Admin Add Employee) ───────────────────────────────────

export function useProvisionUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: ProvisionUserPayload): Promise<ProvisionUserResponse> => {
      const response = await apiClient.post<ProvisionUserResponse>(
        '/api/v1/admin/provision-user/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.all });
    },
  });
}
